/* eslint eqeqeq: [2, "smart"] */
import { Env, Locale } from '@shopee-user/global';
import { Feature } from './constants';
import { FeatureToggleConfig } from './types';
import { getDeviceFingerprint } from '@shopee-user/device-fingerprint';

// calculate once for consistency
const currentTimestamp = Math.floor(new Date().getTime() / 1000);

export function isFeatureEnabled(
  feature: Feature,
  featureToggleConfig: Partial<FeatureToggleConfig>,
  {
    defaultValue,
    deviceFingerprint,
    ENV,
    LOCALE,
  }: {
    defaultValue: boolean;
    deviceFingerprint?: number;
    ENV: Env;
    LOCALE: Locale;
  }
): boolean {
  const envToLocaleToConfig = featureToggleConfig[feature];

  // 1. if envToLocaleToConfig is undefined or boolean, returns immediately
  if (envToLocaleToConfig === undefined) {
    return defaultValue;
  }

  if (typeof envToLocaleToConfig === 'boolean') {
    return envToLocaleToConfig;
  }

  const localeToConfig =
    envToLocaleToConfig[ENV] ??
    envToLocaleToConfig['default_env'] ??
    envToLocaleToConfig['default'] ??
    undefined;

  // 2. else, if localeToConfig is undefined or boolean, returns immediately
  if (localeToConfig === undefined) {
    return defaultValue;
  }

  if (typeof localeToConfig === 'boolean') {
    return localeToConfig;
  }

  const config =
    localeToConfig[LOCALE] ??
    localeToConfig['default_locale'] ??
    localeToConfig['default'] ??
    undefined;

  // 3. else, if config is undefined or boolean, returns immediately
  if (config === undefined) {
    return defaultValue;
  }

  if (typeof config === 'boolean') {
    return config;
  }

  // 4. else, if currentTimestamp is not in [start_time, end_time), returns defaultValue
  if (config.start_time != null && currentTimestamp < config.start_time) {
    return defaultValue;
  }

  if (config.end_time != null && currentTimestamp >= config.end_time) {
    return defaultValue;
  }

  // 5. else, for each rule, if matches, then returns
  if (Array.isArray(config.rules)) {
    for (const rule of config.rules) {
      if (rule.disabled === true) {
        continue;
      }
      if (rule.start_time != null && currentTimestamp < rule.start_time) {
        continue;
      }
      if (rule.end_time != null && currentTimestamp >= rule.end_time) {
        continue;
      }
      const df = deviceFingerprint ?? getDeviceFingerprint();
      if (df % 100 < rule.percentage) return rule.value;
    }
  }

  // 6. else, return config.default
  return config.default;
}
